import { initialize } from 'redux-form';
import { findIndex, isEqual, last, size } from 'lodash/fp';
import {
    createAction,
    multiplyNumbersAsDecimals,
    DateTimeService,
    WallClockDateService,
    TimeService,
} from '@mews/framework';
import { CommanderThunkAction } from '@mews-commander/types';
import { TimeUnit } from '@mews/enums';
import { dateTimeToLocalizedDate } from '@mews-commander/utils';
import {
    getCultureOrderedDayEnumNames,
    getCurrentDateTimeLocalizationInfo,
} from '@mews-commander/core/legacy-globalization';
import { BASE_PRICE_ADJUSTMENTS_SIDEBAR, HIDE_SYMBOL_OPTION, PERCENTAGE_PRECISION } from '../constants';
import { prefix } from '../module';
import {
    getAttributePriceAdjustmentFormName,
    getRateAdjustmentFormName,
    getBasePriceCurrency,
    getTimeUnitRangeArray,
    getHighlightedCellRange,
    getSelectedPricingPrices,
    getLocalizedHourValues,
    getSelectedServiceTimeUnit,
} from '../selectors';

export const SIDEBAR_OPENED = prefix('SIDEBAR_OPENED');
export const SIDEBAR_CLOSED = prefix('SIDEBAR_CLOSED');
export const ATTRIBUTE_ID_SELECTED = prefix('ATTRIBUTE_ID_SELECTED');

export const HIGHLIGHTED_CELL_RANGE_SET = prefix('HIGHLIGHTED_CELL_RANGE_SET');

export const setHighlightedCellRange =
    ({
        adjustmentStartDate,
        adjustmentEndDate,
        adjustmentStartDateTime,
        adjustmentEndDateTime,
    }: $TSFixMe): CommanderThunkAction =>
    (dispatch, getState, { container }) => {
        const {
            appLocalizationComponent: { parseDate, parseDateAndShortTime },
        } = container();

        const localization = getCurrentDateTimeLocalizationInfo(getState());
        const isValidDateString = (dateString: string) => WallClockDateService.isValidString(dateString, localization);

        const isValidTimeString = (dateTimeString: string) =>
            TimeService.isValidShortString(dateTimeString, localization);

        if (isValidDateString(adjustmentStartDate) && isValidDateString(adjustmentEndDate)) {
            const state = getState();
            // @ts-expect-error $TSFixMeError: Expected 2 arguments, but got 1.
            const timeUnit = getSelectedServiceTimeUnit(state);
            const dateTimeRangeArray = getTimeUnitRangeArray(state, { timeUnit });

            const isHourlyService =
                timeUnit === TimeUnit.Hour &&
                isValidTimeString(adjustmentStartDateTime) &&
                isValidTimeString(adjustmentEndDateTime);

            const startDate = parseDate(adjustmentStartDate);
            const endDate = parseDate(adjustmentEndDate);

            const parsedStartDateTime = isHourlyService
                ? parseDateAndShortTime(adjustmentStartDate, adjustmentStartDateTime)
                : startDate;
            const parsedEndDateTime = isHourlyService
                ? parseDateAndShortTime(adjustmentEndDate, adjustmentEndDateTime)
                : endDate;

            const startIndex = findIndex(isEqual(parsedStartDateTime), dateTimeRangeArray);
            const endIndex = findIndex(isEqual(parsedEndDateTime), dateTimeRangeArray);

            if (
                startIndex === -1 &&
                endIndex === -1 &&
                !(
                    DateTimeService.isLess(parsedStartDateTime, dateTimeRangeArray[0]) &&
                    // @ts-expect-error $TSFixMeError: Argument of type 'DateTime | undefined' is not assignable to parameter of type 'DateTime'.
                    DateTimeService.isGreater(parsedEndDateTime, last(dateTimeRangeArray))
                )
            ) {
                const isDayAfterAdjustmentEndDateInRange = DateTimeService.isEqual(
                    timeUnit === TimeUnit.Hour
                        ? DateTimeService.addHours(parsedEndDateTime, 1)
                        : DateTimeService.addDays(parsedEndDateTime, 1),
                    dateTimeRangeArray[0]
                );
                dispatch(
                    createAction(HIGHLIGHTED_CELL_RANGE_SET, {
                        startIndex: Number.POSITIVE_INFINITY,
                        endIndex: isDayAfterAdjustmentEndDateInRange ? -1 : Number.NEGATIVE_INFINITY,
                    })
                );
            } else {
                dispatch(
                    createAction(HIGHLIGHTED_CELL_RANGE_SET, {
                        startIndex,
                        endIndex: endIndex === -1 ? Number.POSITIVE_INFINITY : endIndex,
                    })
                );
            }
        }
    };

export const openBasePriceForm = () => createAction(SIDEBAR_OPENED, BASE_PRICE_ADJUSTMENTS_SIDEBAR);

const initializeRateAdjustmentForm =
    ({ initialDateTime, initialPriceValue }: $TSFixMe): CommanderThunkAction =>
    (dispatch, getState, { container }) => {
        const state = getState();
        const {
            appLocalizationComponent: { localizeShortTime, localizeCurrencyValue },
        } = container();

        const formName = getRateAdjustmentFormName(state);
        const currency = getBasePriceCurrency(state);

        const adjustmentStartDate = dateTimeToLocalizedDate(initialDateTime);
        const adjustmentEndDate = dateTimeToLocalizedDate(initialDateTime);
        const adjustmentStartDateTime = localizeShortTime(initialDateTime);
        const adjustmentEndDateTime = localizeShortTime(initialDateTime);
        const days = getCultureOrderedDayEnumNames(state);
        const hours = getLocalizedHourValues(state);

        dispatch(
            setHighlightedCellRange({
                adjustmentStartDate,
                adjustmentEndDate,
                adjustmentStartDateTime,
                adjustmentEndDateTime,
            })
        );
        dispatch(
            initialize(formName, {
                value: localizeCurrencyValue({ value: initialPriceValue, currency }, HIDE_SYMBOL_OPTION),
                adjustmentStartDate,
                adjustmentEndDate,
                adjustmentStartDateTime,
                adjustmentEndDateTime,
                days,
                hours,
            })
        );
    };

export const openRateAdjustmentForm =
    ({ value, cellIndex, id }: $TSFixMe): CommanderThunkAction =>
    (dispatch: $TSFixMe, getState: $TSFixMe) => {
        const state = getState();
        const formName = getRateAdjustmentFormName(state);
        // @ts-expect-error $TSFixMeError: Expected 2 arguments, but got 1.
        const timeUnit = getSelectedServiceTimeUnit(state);
        const dateTimeRangeArray = getTimeUnitRangeArray(state, { timeUnit });
        dispatch(
            initializeRateAdjustmentForm({
                initialDateTime: dateTimeRangeArray[cellIndex],
                initialPriceValue: value,
            })
        );
        dispatch(createAction(ATTRIBUTE_ID_SELECTED, id));
        dispatch(createAction(SIDEBAR_OPENED, formName));
    };

export const initializeNextRateAdjustmentForm = (): CommanderThunkAction => (dispatch, getState) => {
    const state = getState();
    const { endIndex }: { endIndex: number } = getHighlightedCellRange(state);
    // @ts-expect-error $TSFixMeError: Expected 2 arguments, but got 1.
    const timeUnit = getSelectedServiceTimeUnit(state);
    const dateTimeRangeArray = getTimeUnitRangeArray(state, { timeUnit });

    const nextCellIndex = endIndex + 1;

    if (
        endIndex === Number.POSITIVE_INFINITY ||
        endIndex === Number.NEGATIVE_INFINITY ||
        nextCellIndex === size(dateTimeRangeArray)
    ) {
        return dispatch(closeSidebar());
    }

    const rowPrices = getSelectedPricingPrices(state);

    const initialDateTime = dateTimeRangeArray[nextCellIndex];
    const initialPrice = rowPrices[nextCellIndex];

    dispatch(initializeRateAdjustmentForm({ initialDateTime, initialPriceValue: initialPrice.value }));
};

export const openAttributePriceAdjustmentForm =
    ({
        absoluteAdjustment,
        relativeAdjustment,
        baseAttributeId,
        id,
    }: {
        absoluteAdjustment: number;
        relativeAdjustment: number;
        baseAttributeId?: string | null;
        id: string;
    }): CommanderThunkAction =>
    (dispatch, getState, { container }) => {
        const state = getState();
        const { appLocalizationComponent } = container();
        const formName = getAttributePriceAdjustmentFormName(state);
        const currency = getBasePriceCurrency(state);

        const localizedAbsoluteAdjustment = appLocalizationComponent.localizeCurrencyValue(
            { value: absoluteAdjustment, currency },
            HIDE_SYMBOL_OPTION
        );

        const localizedRelativeAdjustment = appLocalizationComponent.localizeNumber(
            multiplyNumbersAsDecimals(relativeAdjustment, 100),
            { precision: PERCENTAGE_PRECISION }
        );

        dispatch(
            initialize(formName, {
                absoluteAdjustment: localizedAbsoluteAdjustment,
                relativeAdjustment: localizedRelativeAdjustment,
                baseAttributeId,
            })
        );
        void dispatch(createAction(ATTRIBUTE_ID_SELECTED, id));
        dispatch(createAction(SIDEBAR_OPENED, formName));
    };

export const closeSidebar = () => createAction(SIDEBAR_CLOSED);
